import { DolsStatus, type ResidentItem, ResidentStatus } from '@lmc/ts-common'
import { Tag } from '@lmc/ui-kit'
import { isDnacprScheduled } from '@lmc/validations/lib/utils/dnacpr'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import type { ResidentItemExtended } from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { shouldDisplayDnacpr } from '../utils/dnacpr'
import { shouldShowDolsBadge } from '../utils/dols'
import { EditTooltip } from './EditTooltip'

type ProfileBadgesProps = {
  resident: ResidentItem
  testId?: string
}

export const ProfileBadges = ({ resident, testId }: ProfileBadgesProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const { isDoLSOverride } = useHomeType()
  const dolsModsAlignment = useFeatureFlags({
    flagNames: ['dols_mods_alignment']
  }).data?.dols_mods_alignment.enabled

  const displayDolsBadge = shouldShowDolsBadge(resident, dolsModsAlignment)

  const residentDnacpr = resident.dnacpr
  const residentDnacprStartDate = resident.dnacprStartDate ?? undefined

  const displayDnacprBadge = shouldDisplayDnacpr(
    residentDnacpr,
    residentDnacprStartDate
  )

  const displayUpcomingDnacprBadge =
    ((resident as ResidentItem & ResidentItemExtended).dnacprScheduled ??
      isDnacprScheduled(residentDnacpr, residentDnacprStartDate)) &&
    !displayDnacprBadge

  const hasAllergies = resident.residentAllergies?.length > 0

  const hasNoKnownAllergies = resident?.noKnownAllergies === true

  const isDummy = resident.status === ResidentStatus.dummy

  return (
    <StyledBadges
      data-testid={testId ? `${testId}-profile-badges` : 'profile-badges'}
    >
      {isDummy && (
        <Tag
          data-testid={
            testId ? `${testId}-dummy-tag` : 'profile-badges-dummy-tag'
          }
        >
          {t(supConstants.DUMMY)}
        </Tag>
      )}
      {(hasAllergies || hasNoKnownAllergies) && (
        <Tag
          color="green"
          data-testid={
            testId ? `${testId}-allergies-tag` : 'profile-badges-allergies-tag'
          }
        >
          {t(
            supConstants[
              hasNoKnownAllergies ? 'NO_KNOWN_ALLERGIES' : 'ALLERGIES'
            ]
          ).toUpperCase()}
        </Tag>
      )}
      {displayUpcomingDnacprBadge && (
        <Tag
          color="gold"
          data-testid={
            testId
              ? `${testId}-upcoming-dnacpr-tag`
              : 'profile-badges-upcoming-dnacpr-tag'
          }
        >
          {t(supConstants.DNACPR_SCHEDULED_TITLE).toUpperCase()}
        </Tag>
      )}
      {displayDnacprBadge && (
        <Tag
          color="red"
          data-testid={
            testId ? `${testId}-dnacpr-tag` : 'profile-badges-dnacpr-tag'
          }
        >
          {t(supConstants.DNACPR).toUpperCase()}
        </Tag>
      )}
      {resident.hasChokingRisk && (
        <Tag
          color="orange"
          data-testid={
            testId
              ? `${testId}-choking-risk-tag`
              : 'profile-badges-choking-risk-tag'
          }
        >
          {t(supConstants.CHOKING_RISK).toUpperCase()}
        </Tag>
      )}
      {displayDolsBadge && (
        <EditTooltip
          title={
            resident.dolsEndDate
              ? t(supConstants.DOLS_ENDING_TOOLTIP, {
                  date: dayjs(resident.dolsEndDate).format('DD/MM/YYYY')
                })
              : null
          }
        >
          <Tag
            color="blue"
            data-testid={
              testId ? `${testId}-dols-tag` : 'profile-badges-dols-tag'
            }
          >
            {isDoLSOverride ? t(supConstants.DOLS) : t(supConstants.LPS)}
          </Tag>
        </EditTooltip>
      )}
    </StyledBadges>
  )
}

const StyledBadges = styled.div`
  display: flex;
  padding-top: 8px;
  height: fit-content;
`
